import type { ShortlistSelection } from "./ShortlistComposer";
import { isAvailableByDate } from "./groupSearchResults";
import { mergeSelectedRentRange } from "./selectedRentRange";

interface SelectableProperty {
  property_id: string;
  property_name: string;
  neighborhood?: string;
  s3_image_url?: string;
  price_range?: { min: number; max: number };
  serve_pricing: boolean;
}

interface SelectableFloorPlan {
  model_name: string;
  floor_plan_id?: string;
  min_price?: number;
  max_price?: number;
  /** ApartmentSearch keeps every returned unit here, leased ones included. */
  units?: Array<{ current_price?: number | null; status?: string | null; available_date?: string }>;
}

/**
 * 🔴 PKT-0943: a floor plan's shortlist rent comes ONLY from rentable units.
 * ApartmentSearch's min_price/max_price span every unit the search returned,
 * `status: "unavailable"` included, so a plan whose in-budget units were all
 * leased persisted their prices as the client's range. When the plan carries
 * units, price only units available by the target move-in date (or today)
 * (none eligible -> no rent); when it carries
 * none, fall back to the plan's own min/max.
 */
export const rentableFloorPlanRange = (
  floorPlan: SelectableFloorPlan,
  targetDate?: Date
): [number | undefined, number | undefined] => {
  const units = Array.isArray(floorPlan.units) ? floorPlan.units : [];
  if (units.length === 0) return [floorPlan.min_price, floorPlan.max_price];
  const prices = units
    .filter((u) => u && isAvailableByDate(u.status, u.available_date, targetDate))
    .map((u) => u.current_price)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v) && v > 0);
  return prices.length ? [Math.min(...prices), Math.max(...prices)] : [undefined, undefined];
};

interface SelectedFloorPlanEntry {
  floorPlan: SelectableFloorPlan;
  property: SelectableProperty;
}

interface SelectedUnitEntry extends SelectedFloorPlanEntry {
  unit: { unique_id: string; current_price?: number; status?: string | null; available_date?: string };
}

/** Status-omitted/date-omitted legacy selections keep their numeric contract.
 * Explicit status or date evidence must satisfy the same current target gate
 * as live search rows. Never replace an ineligible selected unit with another.
 */
export const rentableSelectedUnitPrice = (
  unit: SelectedUnitEntry["unit"], targetDate?: Date
): number | undefined => {
  const legacyWithoutAvailability = unit.status == null && !unit.available_date;
  if (!legacyWithoutAvailability && !isAvailableByDate(unit.status, unit.available_date, targetDate)) return undefined;
  return typeof unit.current_price === "number" && Number.isFinite(unit.current_price) && unit.current_price > 0
    ? unit.current_price : undefined;
};

/**
 * Convert live search selections into the persistent shortlist payload.
 * Freshness is checked again here so restored/stale browser state cannot put
 * hidden pricing or unit inventory into the #272 shortlist workflow.
 */
export const buildFreshShortlistSelections = (
  selectionMode: "floorplan" | "unit",
  selectedFloorPlans: Map<string, SelectedFloorPlanEntry>,
  selectedUnits: Map<string, SelectedUnitEntry>,
  targetDate?: Date
): ShortlistSelection[] => {
  const grouped = new Map<string, ShortlistSelection>();
  const ensureProperty = (property: SelectableProperty) => {
    if (property.serve_pricing !== true) return null;
    const existing = grouped.get(property.property_id);
    if (existing) return existing;
    const next: ShortlistSelection = {
      propertyId: property.property_id,
      propertyName: property.property_name,
      neighborhood: property.neighborhood,
      imageUrl: property.s3_image_url,
      minRent: undefined,
      maxRent: undefined,
      floorPlanIds: [],
      floorPlanNames: [],
      selectedUnitKeys: [],
    };
    grouped.set(property.property_id, next);
    return next;
  };
  const includeSelectedRent = (
    selection: ShortlistSelection,
    minRent?: number,
    maxRent?: number
  ) => {
    const nextRange = mergeSelectedRentRange(selection, minRent, maxRent);
    selection.minRent = nextRange.minRent;
    selection.maxRent = nextRange.maxRent;
  };

  if (selectionMode === "floorplan") {
    selectedFloorPlans.forEach(({ floorPlan, property }) => {
      const selection = ensureProperty(property);
      if (!selection) return;
      if (floorPlan.floor_plan_id && !selection.floorPlanIds.includes(floorPlan.floor_plan_id)) {
        selection.floorPlanIds.push(floorPlan.floor_plan_id);
      }
      if (!selection.floorPlanNames.includes(floorPlan.model_name)) {
        selection.floorPlanNames.push(floorPlan.model_name);
      }
      includeSelectedRent(selection, ...rentableFloorPlanRange(floorPlan, targetDate));
    });
  } else {
    selectedUnits.forEach(({ unit, floorPlan, property }) => {
      const selection = ensureProperty(property);
      if (!selection) return;
      if (floorPlan.floor_plan_id && !selection.floorPlanIds.includes(floorPlan.floor_plan_id)) {
        selection.floorPlanIds.push(floorPlan.floor_plan_id);
      }
      if (!selection.floorPlanNames.includes(floorPlan.model_name)) {
        selection.floorPlanNames.push(floorPlan.model_name);
      }
      if (!selection.selectedUnitKeys.includes(unit.unique_id)) {
        selection.selectedUnitKeys.push(unit.unique_id);
      }
      const price = rentableSelectedUnitPrice(unit, targetDate);
      includeSelectedRent(selection, price, price);
    });
  }

  return Array.from(grouped.values());
};
